// Chamadas de chat à API própria via lib/api (cookie httpOnly flui pelo proxy /api)
import { api, ApiError } from './api';
import type { ApiChatMessage, ApiChatSession } from '../types/chat';

interface CreateSessionInput {
  sessionType: 'ai' | 'consultant';
  consultantId?: string;
}

interface SendMessageAiResponse {
  userMsg: ApiChatMessage;
  aiMsg: ApiChatMessage;
  credits: number;
}

interface SendMessageConsultantResponse {
  userMsg: ApiChatMessage;
  credits: number;
}

export type SendMessageResponse = SendMessageAiResponse | SendMessageConsultantResponse;

interface MessagesResponse {
  messages: ApiChatMessage[];
}

interface SessionsResponse {
  sessions: ApiChatSession[];
}

interface SessionResponse {
  session: ApiChatSession;
}

export const chatApi = {
  listSessions: () => api.get<SessionsResponse>('/chat/sessions'),
  createSession: (input: CreateSessionInput) => api.post<SessionResponse>('/chat/sessions', input),
  getMessages: (sessionId: string) => api.get<MessagesResponse>(`/chat/sessions/${sessionId}/messages`),
  sendMessage: (sessionId: string, content: string) =>
    api.post<SendMessageResponse>(`/chat/sessions/${sessionId}/messages`, { content }),
};

// ---- streaming da sessão IA (POST responde text/event-stream) ----
// EventSource não faz POST — leitura com fetch + ReadableStream.
export interface AiStreamHandlers {
  onUser?: (userMsg: ApiChatMessage) => void;
  onDelta?: (text: string) => void;
}

export interface AiStreamResult {
  aiMsg: ApiChatMessage;
  credits: number;
}

function parseSsePayload(payload: string):
  | { kind: 'user'; userMsg: ApiChatMessage }
  | { kind: 'delta'; text: string }
  | { kind: 'done'; aiMsg: ApiChatMessage; credits: number }
  | { kind: 'error'; message: string }
  | null {
  try {
    const event = JSON.parse(payload) as {
      type?: string;
      userMsg?: ApiChatMessage;
      text?: string;
      aiMsg?: ApiChatMessage;
      credits?: number;
      message?: string;
    };
    switch (event.type) {
      case 'user':
        return event.userMsg ? { kind: 'user', userMsg: event.userMsg } : null;
      case 'delta':
        return typeof event.text === 'string' ? { kind: 'delta', text: event.text } : null;
      case 'done':
        return event.aiMsg && typeof event.credits === 'number'
          ? { kind: 'done', aiMsg: event.aiMsg, credits: event.credits }
          : null;
      case 'error':
        return { kind: 'error', message: event.message ?? 'Não consegui responder agora. Seu crédito foi devolvido.' };
      default:
        return null;
    }
  } catch {
    return null; // frame malformado — ignora
  }
}

export async function sendAiMessage(
  sessionId: string,
  content: string,
  handlers: AiStreamHandlers = {},
): Promise<AiStreamResult> {
  const res = await fetch(`/api/chat/sessions/${sessionId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });

  if (!res.ok || !res.body) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // corpo não-JSON (ex.: timeout do proxy) — corpo nulo
    }
    throw new ApiError(res.status, body);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  return new Promise<AiStreamResult>((resolve, reject) => {
    let settled = false;

    const handleLine = (line: string) => {
      if (!line.startsWith('data:')) return;
      const parsed = parseSsePayload(line.slice(5).trim());
      if (!parsed) return;

      switch (parsed.kind) {
        case 'user':
          handlers.onUser?.(parsed.userMsg);
          break;
        case 'delta':
          handlers.onDelta?.(parsed.text);
          break;
        case 'done':
          if (!settled) {
            settled = true;
            resolve({ aiMsg: parsed.aiMsg, credits: parsed.credits });
          }
          break;
        case 'error':
          if (!settled) {
            settled = true;
            reject(new ApiError(500, { error: parsed.message }, parsed.message));
          }
          break;
      }
    };

    const drainFrame = (frame: string) => {
      frame.split('\r\n').forEach(handleLine);
    };

    const readAll = async () => {
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          frames.forEach(drainFrame);
        }
        // último frame sem \n\n no fim
        if (buffer) drainFrame(buffer);

        if (!settled) {
          settled = true;
          reject(new ApiError(500, { error: 'Stream encerrado sem resposta completa' }, 'Stream encerrado sem resposta completa'));
        }
      } catch (err) {
        if (!settled) {
          settled = true;
          reject(new ApiError(0, { error: 'stream-error' }, err instanceof Error ? err.message : 'Falha no stream'));
        }
      }
    };

    void readAll();
  });
}